import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { vcollectEnumeratorsApi, ENUMERATOR_STATUS_LABELS } from '../../api/vcollect-enumerator';
import { vcollectProjectsApi } from '../../api/vcollect';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { EmployeePicker } from '../../components/EmployeePicker';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';

interface CreateEnumeratorForm { name: string; phone?: string; email?: string; supervisorEmployeeId?: number; startDate?: string; }

export function EnumeratorsListPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset } = useForm<CreateEnumeratorForm>();

  const { data, isLoading, isError } = useQuery({ queryKey: ['vcollect-enumerators'], queryFn: () => vcollectEnumeratorsApi.list() });
  const { data: vcollectProjectsData } = useQuery({ queryKey: ['vcollect-projects'], queryFn: () => vcollectProjectsApi.list() });
  const vcollectProjects = vcollectProjectsData?.data ?? [];

  const [provisioningId, setProvisioningId] = useState<number | null>(null);
  const [provisionProjectId, setProvisionProjectId] = useState('');
  const [provisionResult, setProvisionResult] = useState<{ token: string } | null>(null);
  const provisionMutation = useMutation({
    mutationFn: () => vcollectEnumeratorsApi.provisionOdk(provisioningId!, Number(provisionProjectId)),
    onSuccess: (r) => {
      showSuccess('ODK Central App User provisioned.');
      setProvisionResult({ token: r.data.token });
      queryClient.invalidateQueries({ queryKey: ['vcollect-enumerators'] });
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateEnumeratorForm) => vcollectEnumeratorsApi.create({ ...input, supervisorEmployeeId: input.supervisorEmployeeId ? Number(input.supervisorEmployeeId) : undefined }),
    onSuccess: (result) => {
      showSuccess(`Enumerator ${result.data.reference} created.`);
      queryClient.invalidateQueries({ queryKey: ['vcollect-enumerators'] });
      setShowCreateForm(false);
      reset();
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const enumerators = data?.data ?? [];

  return (
    <div className="page">
      <PageHeader title="VCollect — Enumerators" description="Field data collectors, who may or may not be full VConnect staff members." />

      <SectionHeader title="Enumerators" actions={
        <>
          <button className="btn-secondary" onClick={() => navigate('/vcollect/teams')}>View Teams</button>
          <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'New Enumerator'}</button>
        </>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
          <label>Name <input {...register('name', { required: true })} /></label>
          <label>Phone <input {...register('phone')} /></label>
          <label>Email <input type="email" {...register('email')} /></label>
          <label>Supervisor <EmployeePicker {...register('supervisorEmployeeId', { valueAsNumber: true })} /></label>
          <label>Start Date <input type="date" {...register('startDate')} /></label>
          <button type="submit" disabled={createMutation.isPending}>Create</button>
        </form>
      )}

      {isError && <div className="error-state">Failed to load enumerators. Please try refreshing the page.</div>}
      {isLoading ? <div className="loading-state"><span className="loading-spinner" />Loading…</div> : (
        <DataTable
          rows={enumerators}
          rowKey={(e) => e.id}
          columns={[
            { header: 'Reference', render: (e) => e.enumerator_reference },
            { header: 'Name', render: (e) => e.name },
            { header: 'Phone', render: (e) => e.phone ?? '-' },
            { header: 'Supervisor', render: (e) => e.supervisor_name ?? '-' },
            { header: 'Status', render: (e) => <StatusBadge status={e.status} label={ENUMERATOR_STATUS_LABELS[e.status]} /> },
            {
              header: '', render: (e) => e.odk_app_user_id ? <span className="muted">ODK Provisioned</span> : (
                <button className="btn-secondary" onClick={() => { setProvisioningId(e.id); setProvisionResult(null); }}>Provision ODK Access</button>
              ),
            },
          ]}
        />
      )}

      {provisioningId && (
        <Modal title="Provision ODK Central App User" onClose={() => { setProvisioningId(null); setProvisionProjectId(''); setProvisionResult(null); }}>
          {!provisionResult ? (
            <>
              <label>Data Collection Project
                <select value={provisionProjectId} onChange={(e) => setProvisionProjectId(e.target.value)}>
                  <option value="">Select…</option>
                  {vcollectProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <div className="form-actions">
                <button onClick={() => provisionMutation.mutate()} disabled={!provisionProjectId || provisionMutation.isPending}>Provision</button>
              </div>
            </>
          ) : (
            <div>
              <div className="tip-box">
                <b>App User created.</b> Copy this token now to configure the enumerator's ODK Collect app - it will not be shown again here.
              </div>
              <pre style={{ background: 'var(--color-page-bg)', padding: 12, borderRadius: 4, marginTop: 10, wordBreak: 'break-all' }}>{provisionResult.token}</pre>
              <p className="muted" style={{ fontSize: 12 }}>A QR-code configuration generator is a future enhancement - for now, this token can be entered manually in ODK Collect's "Configure with QR code or text" option.</p>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
